<template>
  <div class="grc-action-plans-list">
    <b-row>
      <b-col cols="12">
        <b-card>
          <template #header>
            <div
              class="d-flex align-items-center justify-content-between flex-wrap grc-gap-2"
            >
              <div>
                <h5 class="mb-0">Planos de Ação</h5>
                <small class="text-muted"
                  >Execução, acompanhamento e encerramento de ações vinculadas
                  ao ecossistema GRC.</small
                >
              </div>
              <div class="d-flex flex-wrap grc-gap-2">
                <b-button
                  size="sm"
                  variant="outline-secondary"
                  :disabled="loading || exporting"
                  @click="exportCsv"
                >
                  {{ exporting ? "Exportando..." : "Exportar CSV" }}
                </b-button>
                <b-button
                  v-if="canCreate"
                  size="sm"
                  variant="primary"
                  @click="openNew"
                >
                  Novo plano de ação
                </b-button>
              </div>
            </div>
          </template>
          <b-card-body>
            <div class="grc-filters-section">
              <b-form-group label="Pesquisar" class="grc-search-filter">
                <b-input-group>
                  <b-input-group-prepend>
                    <b-input-group-text>
                      <i class="fas fa-search" aria-hidden="true"></i>
                    </b-input-group-text>
                  </b-input-group-prepend>
                  <b-form-input
                    v-model="filters.search"
                    type="search"
                    placeholder="Título ou ação"
                    @keyup.enter="applyFilters"
                  ></b-form-input>
                  <b-input-group-append>
                    <b-button :disabled="!filters.search" @click="clearSearch">
                      Limpar
                    </b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
              <b-form-group label="Status">
                <b-form-select
                  v-model="filters.status"
                  :options="statusFilterOptions"
                ></b-form-select>
              </b-form-group>
              <b-form-group label="Área">
                <b-form-select
                  v-model="filters.area"
                  :options="areaFilterOptions"
                ></b-form-select>
              </b-form-group>
              <b-button variant="outline-secondary" @click="applyFilters">
                Filtrar
              </b-button>
            </div>

            <b-alert
              v-if="activeStatusSummary"
              show
              variant="info"
              class="py-2"
            >
              Recorte do dashboard: {{ activeStatusSummary }}
            </b-alert>

            <GRCListTable
              :items="data.docs"
              :fields="fields"
              :busy="loading"
              :page="data.page"
              :total-rows="data.total"
              :per-page="data.limit"
              :sort-by="sortBy"
              :sort-desc="sortDesc"
              server-sorting
              empty-text="Nenhum plano de ação encontrado"
              busy-text="Carregando planos de ação..."
              @sort-changed="onSortChanged"
              @page-change="changePage"
            >
              <template #cell(title)="slotData">
                <div>
                  <strong>{{ slotData.item.title }}</strong>
                  <div class="text-muted small">
                    {{ slotData.item.whatWillBeDone }}
                  </div>
                </div>
              </template>
              <template #cell(area)="slotData">
                {{ slotData.item.area ? slotData.item.area.name : "-" }}
              </template>
              <template #cell(priority)="slotData">
                <b-badge :variant="priorityVariant(slotData.item.priority)">
                  {{ priorityLabel(slotData.item.priority) }}
                </b-badge>
              </template>
              <template #cell(progress)="slotData">
                {{ slotData.item.progress }}%
              </template>
              <template #cell(status)="slotData">
                <b-badge :variant="statusVariant(slotData.item.status)">
                  {{ statusLabel(slotData.item.status) }}
                </b-badge>
              </template>
              <template #cell(dueDate)="slotData">
                {{ formatDate(slotData.item.dueDate) }}
              </template>
              <template #cell(actions)="slotData">
                <div class="grc-action-buttons">
                  <b-button
                    v-if="canEdit"
                    size="sm"
                    variant="link"
                    class="grc-icon-button grc-icon-button--edit"
                    title="Editar"
                    aria-label="Editar"
                    @click="openEdit(slotData.item)"
                    ><i class="fa fa-edit" aria-hidden="true"></i
                  ></b-button>
                  <b-button
                    v-if="
                      canApprove &&
                      getApprovalStatus(slotData.item) === 'pending'
                    "
                    size="sm"
                    variant="outline-success"
                    @click="approve(slotData.item)"
                  >
                    Aprovar
                  </b-button>
                  <b-button
                    v-if="
                      canApprove &&
                      getApprovalStatus(slotData.item) === 'pending'
                    "
                    size="sm"
                    variant="outline-warning"
                    @click="reject(slotData.item)"
                  >
                    Rejeitar
                  </b-button>
                  <b-button
                    v-if="canDelete"
                    size="sm"
                    variant="link"
                    class="grc-icon-button grc-icon-button--delete"
                    title="Inativar"
                    aria-label="Inativar"
                    @click="remove(slotData.item)"
                    ><i class="fa fa-trash" aria-hidden="true"></i
                  ></b-button>
                </div>
              </template>
              <template #empty>
                <div class="text-muted text-center py-5">
                  <i class="fas fa-tasks fa-2x"></i>
                  <p class="mt-3 mb-0">Nenhum plano de ação encontrado</p>
                </div>
              </template>
            </GRCListTable>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <GRCFormSidebar
      id="grc-action-plan-sidebar"
      :visible.sync="showSidebar"
      :title="
        selectedActionPlan ? 'Editar plano de ação' : 'Novo plano de ação'
      "
      @hidden="onModalHidden"
    >
      <GRCActionPlanForm
        :action-plan="selectedActionPlan"
        :area-options="areaOptions"
        :scope-options="scopeOptions"
        :project-options="projectOptions"
        :requirement-options="requirementOptions"
        :risk-options="riskOptions"
        :control-options="controlOptions"
        :user-options="userOptions"
        :saving="saving"
        @submit="saveActionPlan"
        @cancel="showSidebar = false"
      />
    </GRCFormSidebar>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { getUsers } from "@/api/user";
import { getGrcMetadata } from "@/api/grc-metadata";
import { exportRowsToCsv } from "@/utils/csv-export";
import { getGrcRequirements } from "@/api/grc-requirements";
import { getGrcRisks } from "@/api/grc-risks";
import { getGrcControls } from "@/api/grc-controls";
import {
  approveGrcActionPlan,
  createGrcActionPlan,
  deleteGrcActionPlan,
  getGrcActionPlan,
  getGrcActionPlans,
  rejectGrcActionPlan,
  updateGrcActionPlan,
} from "@/api/grc-action-plans";
import GRCFormSidebar from "./components/GRCFormSidebar.vue";
import GRCListTable from "./components/GRCListTable.vue";
import GRCActionPlanForm from "./GRCActionPlanForm.vue";

const STATUS = {
  PLANNED: 1,
  IN_PROGRESS: 2,
  COMPLETED: 3,
  WAITING_APPROVAL: 4,
  SUSPENDED: 5,
};

export default {
  name: "GRCActionPlansList",
  components: {
    GRCFormSidebar,
    GRCListTable,
    GRCActionPlanForm,
  },
  data() {
    return {
      loading: false,
      exporting: false,
      saving: false,
      sortBy: "dueDate",
      sortDesc: false,
      showSidebar: false,
      selectedActionPlan: null,
      fields: [
        {
          key: "_id",
          label: "#",
          sortable: true,
          thStyle: { width: "90px !important" },
        },
        {
          key: "title",
          label: "Plano de ação",
          sortable: true,
          thStyle: { "min-width": "240px !important" },
        },
        {
          key: "area",
          label: "Área",
          thStyle: { "min-width": "170px !important" },
        },
        {
          key: "priority",
          label: "Prioridade",
          sortable: true,
          thStyle: { width: "150px !important" },
        },
        {
          key: "progress",
          label: "Progresso",
          sortable: true,
          thStyle: { width: "140px !important" },
        },
        {
          key: "status",
          label: "Status",
          sortable: true,
          thStyle: { width: "170px !important" },
        },
        {
          key: "dueDate",
          label: "Prazo",
          sortable: true,
          thStyle: { width: "160px !important" },
        },
        {
          key: "actions",
          label: "Ações",
          thStyle: { width: "220px !important" },
        },
      ],
      filters: {
        search: "",
        status: null,
        area: null,
      },
      data: {
        docs: [],
        total: 0,
        page: 1,
        pages: 0,
        limit: 10,
      },
      areaOptions: [],
      scopeOptions: [],
      projectOptions: [],
      requirementOptions: [],
      riskOptions: [],
      controlOptions: [],
      userOptions: [],
      routeStatusValues: [],
    };
  },
  computed: {
    ...mapGetters(["profile", "permissions"]),
    currentCompanyId() {
      const companyId = parseInt(this.$route.query.company, 10);
      return Number.isNaN(companyId) ? null : companyId;
    },
    statusFilterOptions() {
      return [
        { value: null, text: "Todos" },
        { value: STATUS.PLANNED, text: "Planejado" },
        { value: STATUS.IN_PROGRESS, text: "Em execução" },
        { value: STATUS.COMPLETED, text: "Concluído" },
        { value: STATUS.WAITING_APPROVAL, text: "Aguardando aprovação" },
        { value: STATUS.SUSPENDED, text: "Suspenso" },
      ];
    },
    areaFilterOptions() {
      return [{ value: null, text: "Todas" }, ...this.areaOptions];
    },
    canCreate() {
      return this.hasActionPlanPermission("create");
    },
    canEdit() {
      return this.hasActionPlanPermission("edit");
    },
    canDelete() {
      return this.hasActionPlanPermission("delete");
    },
    canApprove() {
      return this.hasActionPlanPermission("approve");
    },
    activeStatusSummary() {
      if (this.routeStatusValues.length <= 1) {
        return "";
      }

      return this.routeStatusValues
        .map((value) => this.statusLabel(value))
        .join(", ");
    },
  },
  watch: {
    "$route.query": {
      async handler(newQuery, oldQuery) {
        const nextCompanyId = this.normalizeQueryNumber(newQuery.company);
        const previousCompanyId = this.normalizeQueryNumber(
          oldQuery && oldQuery.company
        );

        this.applyRouteState();

        if (nextCompanyId !== previousCompanyId) {
          await this.loadSupportData();
        }

        await this.loadActionPlans();
      },
    },
  },
  async created() {
    this.applyRouteState();
    await this.loadSupportData();
    await this.loadActionPlans();
  },
  methods: {
    normalizeQueryNumber(value) {
      const parsed = parseInt(value, 10);
      return Number.isNaN(parsed) ? null : parsed;
    },
    parseQueryNumberList(value) {
      if (value === null || value === undefined || value === "") {
        return [];
      }

      const rawValues = Array.isArray(value) ? value : [value];
      return Array.from(
        new Set(
          rawValues
            .flatMap((entry) => String(entry).split(","))
            .map((entry) => this.normalizeQueryNumber(entry))
            .filter(Boolean)
        )
      );
    },
    getRequestParams(extra = {}) {
      return {
        ...extra,
        company: this.currentCompanyId || undefined,
      };
    },
    getListParams(extra = {}) {
      return this.getRequestParams({
        page: this.data.page,
        size: this.data.limit,
        sortBy: this.sortBy || undefined,
        sortDesc: this.sortDesc,
        search: this.filters.search || undefined,
        status:
          this.routeStatusValues.length <= 1
            ? this.filters.status || undefined
            : undefined,
        statuses:
          this.routeStatusValues.length > 1
            ? this.routeStatusValues.join(",")
            : undefined,
        area: this.filters.area || undefined,
        ...extra,
      });
    },
    getMutationPayload(payload) {
      if (!this.currentCompanyId) {
        return payload;
      }

      return {
        ...payload,
        company: this.currentCompanyId,
      };
    },
    applyRouteState() {
      const query = this.$route.query || {};
      const statuses = this.parseQueryNumberList(
        query.statuses || query.status
      );

      this.filters.search =
        typeof query.search === "string" ? query.search : "";
      this.routeStatusValues = statuses;
      this.filters.status = statuses.length === 1 ? statuses[0] : null;
      this.filters.area = this.normalizeQueryNumber(query.area);
      this.data.page = this.normalizeQueryNumber(query.page) || 1;
    },
    buildRouteQuery(overrides = {}) {
      const search = Object.prototype.hasOwnProperty.call(overrides, "search")
        ? overrides.search
        : this.filters.search;
      const status = Object.prototype.hasOwnProperty.call(overrides, "status")
        ? overrides.status
        : this.filters.status;
      const statuses = Object.prototype.hasOwnProperty.call(
        overrides,
        "statuses"
      )
        ? this.parseQueryNumberList(overrides.statuses)
        : status
        ? []
        : this.routeStatusValues;
      const area = Object.prototype.hasOwnProperty.call(overrides, "area")
        ? overrides.area
        : this.filters.area;
      const page = Object.prototype.hasOwnProperty.call(overrides, "page")
        ? overrides.page
        : this.data.page;
      const company = Object.prototype.hasOwnProperty.call(overrides, "company")
        ? overrides.company
        : this.currentCompanyId;
      const query = {};

      if (company) {
        query.company = String(company);
      }
      if (search) {
        query.search = search;
      }
      if (status) {
        query.status = String(status);
      } else if (statuses.length > 1) {
        query.statuses = statuses.join(",");
      }
      if (area) {
        query.area = String(area);
      }
      if (page && page > 1) {
        query.page = String(page);
      }

      return query;
    },
    hasSameRouteQuery(query) {
      return ["company", "search", "status", "statuses", "area", "page"].every(
        (key) => {
          const currentValue =
            this.$route.query[key] === undefined
              ? null
              : String(this.$route.query[key]);
          const nextValue =
            query[key] === undefined ? null : String(query[key]);
          return currentValue === nextValue;
        }
      );
    },
    syncRouteQuery(overrides = {}) {
      const query = this.buildRouteQuery(overrides);

      if (this.hasSameRouteQuery(query)) {
        this.applyRouteState();
        return this.loadActionPlans();
      }

      return this.$router.replace({
        path: this.$route.path,
        query,
      });
    },
    mapUserOptions(users) {
      const scopedUsers = this.currentCompanyId
        ? users.filter((item) => {
            const companyId = this.normalizeQueryNumber(
              item.company?._id || item.company
            );
            return companyId === this.currentCompanyId;
          })
        : users;

      return scopedUsers.map((item) => ({
        value: item._id,
        text: item.fullname || `${item.name} ${item.surname || ""}`.trim(),
      }));
    },
    hasActionPlanPermission(permission) {
      const role = this.profile.role;
      if (role === "company-root" || role === "root") {
        return true;
      }

      if (parseInt(role) === role) {
        return Boolean(this.permissions?.actionPlans?.[permission]);
      }

      return false;
    },
    async loadSupportData() {
      try {
        const [
          areasResponse,
          scopesResponse,
          projectsResponse,
          requirementsResponse,
          risksResponse,
          controlsResponse,
          usersResponse,
        ] = await Promise.all([
          getGrcMetadata("areas", this.getRequestParams()),
          getGrcMetadata("scopes", this.getRequestParams()),
          getGrcMetadata("projects", this.getRequestParams()),
          getGrcRequirements(this.getRequestParams({ page: 1, size: 200 })),
          getGrcRisks(this.getRequestParams({ page: 1, size: 200 })),
          getGrcControls(this.getRequestParams({ page: 1, size: 200 })),
          getUsers({ page: 1, size: 200 }),
        ]);

        this.areaOptions = areasResponse.data.map((item) => ({
          value: item._id,
          text: item.code ? `${item.code} - ${item.name}` : item.name,
          selectedText: item.name,
        }));
        this.scopeOptions = scopesResponse.data.map((item) => ({
          value: item._id,
          text: item.area?.name
            ? `${item.name} (${item.area.name})`
            : item.name,
          selectedText: item.name,
        }));
        this.projectOptions = projectsResponse.data.map((item) => ({
          value: item._id,
          text: item.code ? `${item.code} - ${item.name}` : item.name,
          selectedText: item.name,
        }));
        this.requirementOptions = (requirementsResponse.data.docs || []).map(
          (item) => ({
            value: item._id,
            text: item.title,
            selectedText: item.title,
          })
        );
        this.riskOptions = (risksResponse.data.docs || []).map((item) => ({
          value: item._id,
          text: `${item.title} (${item.score})`,
          selectedText: item.title,
        }));
        this.controlOptions = (controlsResponse.data.docs || []).map(
          (item) => ({
            value: item._id,
            text: item.name,
            selectedText: item.name,
          })
        );
        this.userOptions = this.mapUserOptions(usersResponse.data.docs || []);
      } catch (error) {
        this.showError(error, "Não foi possível carregar os dados auxiliares");
      }
    },
    async loadActionPlans() {
      this.loading = true;
      try {
        const response = await getGrcActionPlans(this.getListParams());
        this.data = response.data;
      } catch (error) {
        this.showError(error, "Não foi possível carregar os planos de ação");
      } finally {
        this.loading = false;
      }
    },
    async exportCsv() {
      this.exporting = true;
      try {
        const response = await getGrcActionPlans(
          this.getListParams({
            page: 1,
            size: Math.max(this.data.total || this.data.limit || 1, 1),
          })
        );
        const rows = (response.data.docs || []).map((item) => [
          item._id,
          item.title,
          item.area ? item.area.name : "-",
          this.priorityLabel(item.priority),
          `${item.progress || 0}%`,
          this.statusLabel(item.status),
          this.formatDate(item.startDate),
          this.formatDate(item.dueDate),
        ]);

        exportRowsToCsv(
          `grc_planos_acao_${new Date()
            .toISOString()
            .slice(0, 19)
            .replace(/[:T]/g, "-")}.csv`,
          [
            "ID",
            "Plano de ação",
            "Área",
            "Prioridade",
            "Progresso",
            "Status",
            "Início",
            "Prazo",
          ],
          rows
        );
      } catch (error) {
        this.showError(error, "Não foi possível exportar os planos de ação");
      } finally {
        this.exporting = false;
      }
    },
    applyFilters() {
      this.data.page = 1;
      this.syncRouteQuery({ page: 1 });
    },
    clearSearch() {
      this.filters.search = "";
      this.applyFilters();
    },
    changePage(page) {
      this.data.page = page;
      this.syncRouteQuery({ page });
    },
    onSortChanged({ sortBy, sortDesc }) {
      this.sortBy = sortBy;
      this.sortDesc = sortDesc;
      this.data.page = 1;
      if (this.$route.query.page) {
        this.syncRouteQuery({ page: 1 });
        return;
      }
      this.loadActionPlans();
    },
    openNew() {
      this.selectedActionPlan = null;
      this.showSidebar = true;
    },
    async openEdit(item) {
      try {
        const response = await getGrcActionPlan(
          item._id,
          this.getRequestParams()
        );
        this.selectedActionPlan = response.data;
        this.showSidebar = true;
      } catch (error) {
        this.showError(error, "Não foi possível carregar o plano de ação");
      }
    },
    onModalHidden() {
      this.selectedActionPlan = null;
    },
    async saveActionPlan(payload) {
      this.saving = true;
      try {
        const requestParams = this.getRequestParams();
        const data = this.getMutationPayload(payload);
        if (this.selectedActionPlan && this.selectedActionPlan._id) {
          await updateGrcActionPlan(
            this.selectedActionPlan._id,
            data,
            requestParams
          );
        } else {
          await createGrcActionPlan(data, requestParams);
        }

        this.$bvToast.toast("Plano de ação salvo com sucesso.", {
          title: "Sucesso",
          toastClass: "custom-toast custom-toast-success",
          variant: "light",
          solid: true,
          autoHideDelay: 5000,
        });
        this.showSidebar = false;
        await this.loadActionPlans();
      } catch (error) {
        this.showError(error, "Não foi possível salvar o plano de ação");
      } finally {
        this.saving = false;
      }
    },
    async approve(item) {
      try {
        await approveGrcActionPlan(item._id, {}, this.getRequestParams());
        this.$bvToast.toast("Plano de ação aprovado com sucesso.", {
          title: "Sucesso",
          toastClass: "custom-toast custom-toast-success",
          variant: "light",
          solid: true,
          autoHideDelay: 5000,
        });
        await this.loadActionPlans();
      } catch (error) {
        this.showError(error, "Não foi possível aprovar o plano de ação");
      }
    },
    async reject(item) {
      const reason = window.prompt("Informe o motivo da rejeição:");
      if (!reason) {
        return;
      }

      try {
        await rejectGrcActionPlan(
          item._id,
          { reason },
          this.getRequestParams()
        );
        this.$bvToast.toast("Plano de ação rejeitado com sucesso.", {
          title: "Sucesso",
          toastClass: "custom-toast custom-toast-success",
          variant: "light",
          solid: true,
          autoHideDelay: 5000,
        });
        await this.loadActionPlans();
      } catch (error) {
        this.showError(error, "Não foi possível rejeitar o plano de ação");
      }
    },
    async remove(item) {
      const confirmed = window.confirm(
        `Deseja inativar o plano de ação ${item.title}?`
      );
      if (!confirmed) {
        return;
      }

      try {
        await deleteGrcActionPlan(item._id, this.getRequestParams());
        this.$bvToast.toast("Plano de ação inativado com sucesso.", {
          title: "Sucesso",
          toastClass: "custom-toast custom-toast-success",
          variant: "light",
          solid: true,
          autoHideDelay: 5000,
        });
        await this.loadActionPlans();
      } catch (error) {
        this.showError(error, "Não foi possível inativar o plano de ação");
      }
    },
    statusLabel(value) {
      const labels = {
        [STATUS.PLANNED]: "Planejado",
        [STATUS.IN_PROGRESS]: "Em execução",
        [STATUS.COMPLETED]: "Concluído",
        [STATUS.WAITING_APPROVAL]: "Aguardando aprovação",
        [STATUS.SUSPENDED]: "Suspenso",
      };
      return labels[value] || "-";
    },
    statusVariant(value) {
      const variants = {
        [STATUS.PLANNED]: "secondary",
        [STATUS.IN_PROGRESS]: "info",
        [STATUS.COMPLETED]: "success",
        [STATUS.WAITING_APPROVAL]: "warning",
        [STATUS.SUSPENDED]: "danger",
      };
      return variants[value] || "secondary";
    },
    priorityLabel(value) {
      const labels = {
        low: "Baixa",
        medium: "Média",
        high: "Alta",
      };
      return labels[value] || "-";
    },
    priorityVariant(value) {
      const variants = {
        low: "secondary",
        medium: "warning",
        high: "danger",
      };
      return variants[value] || "secondary";
    },
    formatDate(value) {
      if (!value) {
        return "-";
      }
      return new Date(value).toLocaleDateString("pt-BR");
    },
    getApprovalStatus(item) {
      return item && item.approval ? item.approval.status : null;
    },
    showError(error, fallback) {
      const message =
        error?.response?.data || error?.response?.statusText || fallback;
      this.$bvToast.toast(message, {
        title: "Erro",
        toastClass: "custom-toast custom-toast-error",
        variant: "light",
        solid: true,
        autoHideDelay: 10000,
      });
    },
  },
};
</script>

<style scoped>
@import "./grc-list.scss";
</style>
